import React from "react";
import ReactMarkdown from "react-markdown";
import { Box, Typography, Link as MuiLink, List, ListItem } from "@mui/material";

export default function MuiMarkdown({ children, sx, className }) {
    return (
        <Box sx={{ typography: "body1", ...sx }} className={className}>
            <ReactMarkdown
                components={{
                    // Headings
                    h1: ({ node, ...props }) => <Typography variant="h4" gutterBottom {...props} />,
                    h2: ({ node, ...props }) => <Typography variant="h5" gutterBottom {...props} />,
                    h3: ({ node, ...props }) => <Typography variant="h6" gutterBottom {...props} />,
                    h4: ({ node, ...props }) => <Typography variant="subtitle1" gutterBottom {...props} />,
                    h5: ({ node, ...props }) => <Typography variant="subtitle2" gutterBottom {...props} />,
                    h6: ({ node, ...props }) => <Typography variant="caption" gutterBottom {...props} />,

                    // Paragraph
                    p: ({ node, ...props }) => <Typography paragraph {...props} />,

                    // Links
                    a: ({ href, children, ...props }) => {
                        const isTel = href.length === 0;
                        const isMail = href?.startsWith("mailto:");

                        return (
                            <MuiLink
                                {...props}
                                href={href ? href : `tel:${children}`}
                                target={isTel || isMail ? "_self" : "_blank"}
                                rel={isTel || isMail ? undefined : "noopener noreferrer"}
                                underline="hover"
                            >
                                {children}
                            </MuiLink>
                        );
                    },

                    // Lists
                    ul: ({ node, ...props }) => <List sx={{ pl: 4, mb: 2 }} {...props} />,
                    ol: ({ node, ...props }) => <List component="ol" sx={{ pl: 4, mb: 2 }} {...props} />,
                    li: ({ node, ...props }) => <ListItem sx={{ display: "list-item", pl: 0, mb: 0.5 }} {...props} />,

                    // Blockquote
                    blockquote: ({ node, ...props }) => (
                        <Box
                            sx={{
                                borderLeft: 4,
                                borderColor: "divider",
                                pl: 2,
                                ml: 0,
                                my: 2,
                                color: "text.secondary",
                            }}
                            {...props}
                        />
                    ),

                    // Code blocks
                    code({ node, inline, className, children, ...props }) {
                        return inline ? (
                            <Box
                                component="code"
                                sx={{
                                    bgcolor: "action.hover",
                                    px: 0.5,
                                    py: 0.2,
                                    borderRadius: 1,
                                    fontFamily: "Monospace",
                                    fontSize: "0.875rem",
                                }}
                                {...props}
                            >
                                {children}
                            </Box>
                        ) : (
                            <Box
                                component="pre"
                                sx={{
                                    bgcolor: "action.hover",
                                    p: 2,
                                    borderRadius: 1,
                                    overflowX: "auto",
                                    fontFamily: "Monospace",
                                    fontSize: "0.875rem",
                                    mb: 2,
                                }}
                                {...props}
                            >
                                <code>{children}</code>
                            </Box>
                        );
                    },

                    // Horizontal rule
                    hr: ({ node, ...props }) => <Box component="hr" sx={{ my: 4 }} {...props} />,

                    // Images
                    img: ({ node, ...props }) => (
                        <Box
                            component="img"
                            sx={{ maxWidth: "100%", display: "block", my: 2 }}
                            {...props}
                        />
                    ),

                    // Tables (opzionale)
                    table: ({ node, ...props }) => (
                        <Box
                            component="table"
                            sx={{ width: "100%", borderCollapse: "collapse", mb: 2 }}
                            {...props}
                        />
                    ),
                    th: ({ node, ...props }) => (
                        <Box component="th" sx={{ borderBottom: 1, p: 1, textAlign: "left" }} {...props} />
                    ),
                    td: ({ node, ...props }) => (
                        <Box component="td" sx={{ borderBottom: 1, p: 1 }} {...props} />
                    ),
                    tr: ({ node, ...props }) => <Box component="tr" {...props} />,
                }}
            >
                {children}
            </ReactMarkdown>
        </Box>
    );
}